import { apiRequest } from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  CampaignPatch,
  ICampaignAttachment,
  ICampaignCreate,
  ICampaignPreview,
  ICampaignQuery,
  IMailCampaign,
  IMailCampaignSummary
} from './mail-campaign-contracts';

/**
 * survey-api's mail campaigns (SR-523 stage 3, `mail.outreach.send` and `responses.browse`). A
 * draft's `PATCH` and its launch quote the campaign's `revision` in `If-Match`; a stale one is 409.
 * A test shares the templates' ten an hour: 429 `retryAfter`.
 */

const CAMPAIGNS = '/api/v1/admin/mail-campaigns';

const campaignPath = (id: string) => `${CAMPAIGNS}/${encodeURIComponent(id)}`;

const quoted = (revision: number) => `"${revision}"`;

function campaignOf(campaign: IMailCampaign): IMailCampaign {
  return {
    ...campaign,
    attachments: campaign.attachments ?? [],
    cc: campaign.cc ?? [],
    tags: campaign.tags ?? [],
    sample: campaign.sample ?? [],
    recipients: {
      ...(campaign.recipients ?? { total: 0, reachable: 0, suppressed: 0, noAddress: 0 }),
      excluded: campaign.recipients?.excluded ?? []
    }
  };
}

export const mailCampaignsApi = {
  async create(body: ICampaignCreate): Promise<IMailCampaign> {
    return campaignOf(await apiRequest<IMailCampaign>(CAMPAIGNS, { method: 'POST', body }));
  },

  async get(id: string, signal?: AbortSignal): Promise<IMailCampaign> {
    return campaignOf(await apiRequest<IMailCampaign>(campaignPath(id), { signal }));
  },

  async list(query: ICampaignQuery, signal?: AbortSignal): Promise<IPage<IMailCampaignSummary>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (Array.isArray(value)) for (const each of value) params.append(key, String(each));
      else if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const text = params.toString();
    const answer = await apiRequest<IPage<IMailCampaignSummary>>(text ? `${CAMPAIGNS}?${text}` : CAMPAIGNS, { signal });
    return { ...answer, items: (answer.items ?? []).map((row) => ({ ...row, tags: row.tags ?? [] })) };
  },

  async patch(id: string, revision: number, patch: CampaignPatch): Promise<IMailCampaign> {
    return campaignOf(
      await apiRequest<IMailCampaign>(campaignPath(id), {
        method: 'PATCH',
        body: patch,
        headers: { 'If-Match': quoted(revision) }
      })
    );
  },

  /** One recipient's mail as the drain would render it — any participant of the snapshot. */
  async preview(id: string, participantId: string, signal?: AbortSignal): Promise<ICampaignPreview> {
    const answer = await apiRequest<ICampaignPreview>(
      `${campaignPath(id)}/preview?${new URLSearchParams({ participantId })}`,
      { method: 'POST', signal }
    );
    return { ...answer, preheader: answer.preheader ?? '', cc: answer.cc ?? [] };
  },

  /** The first recipient's mail, to the caller's own address. */
  test(id: string): Promise<{ to: string }> {
    return apiRequest<{ to: string }>(`${campaignPath(id)}/test`, { method: 'POST' });
  },

  launch(id: string, revision: number): Promise<{ status: string }> {
    return apiRequest<{ status: string }>(`${campaignPath(id)}/launch`, {
      method: 'POST',
      headers: { 'If-Match': quoted(revision) }
    });
  },

  /** The queued mails not yet handed to the provider are skipped as cancelled. */
  cancel(id: string): Promise<unknown> {
    return apiRequest<unknown>(`${campaignPath(id)}/cancel`, { method: 'POST' });
  },

  /** A draft, and its attachments with it. */
  remove(id: string): Promise<void> {
    return apiRequest<void>(campaignPath(id), { method: 'DELETE' });
  },

  attach(id: string, file: File): Promise<ICampaignAttachment> {
    const form = new FormData();
    form.append('file', file);
    return apiRequest<ICampaignAttachment>(`${campaignPath(id)}/attachments`, { method: 'POST', body: form });
  },

  detach(id: string, attachmentId: string): Promise<void> {
    return apiRequest<void>(`${campaignPath(id)}/attachments/${encodeURIComponent(attachmentId)}`, {
      method: 'DELETE'
    });
  }
};
